import { FORMULA_OPERATIONS } from "../interfaces/model/enumerations/FormulaOperations";
import { IShapeGuideFormula } from "../interfaces/model/geometry/ShapeGuide";
import { angleInDegree, angleInRadian } from "./geometry";

/**
 * Evaluate formula based on operation.
 *
 * ECMA-376, Third Edition, Part 1 | Section: 20.1.9.11
 *
 * @param formula1 The shape guide formula
 * @param accumulator Accumulator Object holding guides with resolved values
 */
export const evaluateFormula = (formula1: IShapeGuideFormula, accumulator: any): number => {
	const formula = { ...formula1 };
	let toReturn: number = NaN;
	let x: number = NaN;
	let y: number = NaN;
	let z: number = NaN;
	if (formula.operands.length > 2) {
		// try parsing operand to float
		z = parseFloat(formula.operands[2] as string);
		// if operand is point to a guide then it would have present in accumulator in previous iteration
		if (isNaN(z)) {
			z = accumulator[formula.operands[2]] as number;
		}
	}
	if (formula.operands.length > 1) {
		// try parsing operand to float
		y = parseFloat(formula.operands[1] as string);
		// if operand is point to a guide then it would have present in accumulator in previous iteration
		if (isNaN(y)) {
			y = accumulator[formula.operands[1]] as number;
		}
	}

	// try parsing operand to float
	x = parseFloat(formula.operands[0] as string);
	// if operand is point to a guide then it would have present in accumulator in previous iteration
	if (isNaN(x)) {
		x = accumulator[formula.operands[0]] as number;
	}

	switch (formula.operation) {
		// ('*/') - Multiply Divide Formula
		// Arguments: 3(fmla = "*/ x y z")
		// Usage: "*/ x y z" = ((x * y) / z) = value of this guide
		case FORMULA_OPERATIONS.MultiplyDivide:
			toReturn = ((x * y) / z);
			break;

			// ('+-') - Add Subtract Formula
			// Arguments: 3(fmla = "+- x y z")
			// Usage: "+- x y z" = ((x + y) - z) = value of this guide
		case FORMULA_OPERATIONS.AddSubtract:
			toReturn = ((x + y) - z);
			break;

			// ('+/') - Add Divide Formula
			// Arguments: 3(fmla = "+/ x y z")
			// Usage: "+/ x y z" = ((x + y) / z) = value of this guide
		case FORMULA_OPERATIONS.AddDivide:
			toReturn = ((x + y) / z);
			break;

			// ('?:') - If Else Formula
			// Arguments: 3(fmla = "?: x y z")
			// Usage: "?: x y z" = if (x > 0), then y = value of this guide,
			// else z = value of this guide
		case FORMULA_OPERATIONS.IfElse:
			toReturn = x > 0 ? y : z;
			break;

			// ('abs') - Absolute Value Formula
			// Arguments: 1(fmla = "abs x")
			// Usage: "abs x" = if (x < 0), then(-1) * x = value of this guide
			// else x = value of this guide
		case FORMULA_OPERATIONS.AbsoluteValue:
			toReturn = Math.abs(x);
			break;

			// ('at2') - ArcTan Formula
			// Arguments: 2(fmla = "at2 x y")
			// Usage: "at2 x y" = arctan(y / x) = value of this guide
		case FORMULA_OPERATIONS.ArcTan:
			toReturn = angleInDegree(Math.atan(y / x));
			break;

			// ('cat2') - Cosine ArcTan Formula
			// Arguments: 3(fmla = "cat2 x y z")
			// Usage: "cat2 x y z" = (x * (cos(arctan(z / y))) = value of this guide
		case FORMULA_OPERATIONS.CosineArcTan:
			toReturn = (x * (Math.cos(Math.atan(z / y))));
			break;

			// ('cos') - Cosine Formula
			// Arguments: 2(fmla = "cos x y")
			// Usage: "cos x y" = (x * cos(y)) = value of this guide
		case FORMULA_OPERATIONS.Cosine:
			toReturn = (x * Math.cos(angleInRadian(y)));
			break;

			//  * ('max') -  Formula
			//  * Arguments: 2(fmla = "max x y")
			// * Usage: "max x y" = if (x > y), then x = value of this guide
			// * else y = value of this guide
		case FORMULA_OPERATIONS.MaximumValue:
			toReturn = Math.max(x, y);
			break;

			// * ('min') - Minimum Value Formula
			//  * Arguments: 2(fmla = "min x y")
			// * Usage: "min x y" = if (x < y), then x = value of this guide
			// * else y = value of this guide
		case FORMULA_OPERATIONS.MinimumValue:
			toReturn = Math.min(x, y);
			break;

			//  * ('mod') - Modulo Formula
			//  * Arguments: 3(fmla = "mod x y z")
			// * Usage: "mod x y z" = sqrt(x ^ 2 + b ^ 2 + c ^ 2) = value of this guide
		case FORMULA_OPERATIONS.Modulo:
			toReturn = Math.sqrt(x * x + y * y + z * z);
			break;

			//  * ('pin') - Pin To Formula
			//  * Arguments: 3(fmla = "pin x y z")
			// * Usage: "pin x y z" = if (y < x), then x = value of this guide
			// * else if (y > z), then z = value of this guide
			// * else y = value of this guide
		case FORMULA_OPERATIONS.PinTo:
			toReturn = (y < x) ? x : ((y > z) ? z : y);
			break;

			// * ('sat2') - Sine ArcTan Formula
			//  * Arguments: 3(fmla = "sat2 x y z")
			// * Usage: "sat2 x y z" = (x * sin(arctan(z / y))) = value of this guide
		case FORMULA_OPERATIONS.SineArcTan:
			toReturn = (x * Math.sin(Math.atan(z / y)));
			break;

			//  * ('sin') - Sine Formula
			//  * Arguments: 2(fmla = "sin x y")
			// * Usage: "sin x y" = (x * sin(y)) = value of this guide
		case FORMULA_OPERATIONS.Sine:
			toReturn = (x * Math.sin(angleInRadian(y)));
			break;

			//  * ('sqrt') - Square Root Formula
			//  * Arguments: 1(fmla = "sqrt x")
			// * Usage: "sqrt x" = sqrt(x) = value of this guide
		case FORMULA_OPERATIONS.SquareRoot:
			toReturn = Math.sqrt(x);
			break;

			// * ('tan') - Tangent Formula
			//  * Arguments: 2(fmla = "tan x y")
			// * Usage: "tan x y" = (x * tan(y)) = value of this guide
		case FORMULA_OPERATIONS.Tangent:
			toReturn = (x * Math.tan(angleInRadian(y)));
			break;

			// * ('val') - Literal Value Formula
			//  * Arguments: 1(fmla = "val x")
			// * Usage: "val x" = x = value of this guide
		case FORMULA_OPERATIONS.LiteralValue:
			toReturn = x;
			break;

		default:
			toReturn = NaN;
	}
	return toReturn;
};
